import { SETTINGS_STORAGE_KEY } from '../game/constants';
import { barFraction, calmLoading, isStalled, LOADING_TEXT as TEXT, percentLabel } from './loadingModel';
import './loadingScreen.css';

const SVG_NS = 'http://www.w3.org/2000/svg';
const FADE_MS = 450;
const STALL_CHECK_MS = 1000;

/** A pixel stack of pancakes with a pat of butter (16×11 cells). */
const PANCAKE_ART = [
  '......YYYY......',
  '.....YyyyyY.....',
  '..ssssssssssss..',
  '.sPPPPPPPPPPPPs.',
  '.sdddddddddddds.',
  '.PPPPPPPPPPPPPP.',
  '.dddddddddddddd.',
  '.PPPPPPPPPPPPPP.',
  '..dddddddddddd..',
  '.cccccccccccccc.',
  '..CCCCCCCCCCCC..',
];
const PANCAKE_COLORS: Record<string, string> = {
  Y: '#e0a83a',
  y: '#fff1a8',
  s: '#a8542a',
  P: '#f4bd6c',
  d: '#c97a35',
  c: '#fff8ef',
  C: '#e2cfe0',
};
const SKY_GLYPHS = ['✦', '♥', '✧', '·', '♥', '✦', '✧', '·', '✦', '♥'];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function pancakeSvg(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${PANCAKE_ART[0]!.length} ${PANCAKE_ART.length}`);
  svg.setAttribute('class', 'pk-load__pancake');
  svg.setAttribute('aria-hidden', 'true');
  PANCAKE_ART.forEach((row, y) => {
    [...row].forEach((cell, x) => {
      const fill = PANCAKE_COLORS[cell];
      if (!fill) return;
      const r = document.createElementNS(SVG_NS, 'rect');
      r.setAttribute('x', String(x));
      r.setAttribute('y', String(y));
      r.setAttribute('width', '1.02');
      r.setAttribute('height', '1.02');
      r.setAttribute('fill', fill);
      svg.appendChild(r);
    });
  });
  return svg;
}

function readSettings(): string | null {
  try {
    return window.localStorage.getItem(SETTINGS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface LoadingFailure {
  /** Short and harmless, for a bug report (never a URL, key or config). */
  code: string;
  /** Tries the failed step again; the screen returns to its progress view. */
  retry: () => void;
}

/**
 * The page-level loading screen (plain HTML/CSS, so it shows before Phaser or
 * any game art exists): a bobbing pancake, a few stars, one message and a
 * progress bar. The bar shows a real percent when given one and slides
 * without a number when not. Stays until hide().
 */
export class LoadingScreen {
  private root: HTMLDivElement | null = null;
  private message!: HTMLParagraphElement;
  private fill!: HTMLDivElement;
  private track!: HTMLDivElement;
  private percent!: HTMLSpanElement;
  private note!: HTMLParagraphElement;
  private errorBox!: HTMLDivElement;
  private errorCode!: HTMLSpanElement;
  private lastProgressAt = 0;
  private lastFraction: number | null = null;
  private stallTimer = 0;
  private retryHandler: (() => void) | null = null;
  private hiding: Promise<void> | null = null;

  get shown(): boolean {
    return this.root !== null && !this.hiding;
  }

  /** Puts the screen up (or keeps it up) with this message and an indeterminate bar. */
  show(message: string = TEXT.preparing, host: HTMLElement = document.body): void {
    if (!this.root || this.hiding) this.mount(host);
    this.clearError();
    this.lastProgressAt = performance.now();
    this.note.hidden = true;
    this.progress(null, message);
  }

  /** `fraction` 0..1, or null when it can't be measured. */
  progress(fraction: number | null, message?: string): void {
    if (!this.root) return;
    if (message) this.message.textContent = message;
    const f = barFraction(fraction);
    if (f !== this.lastFraction) {
      this.lastFraction = f;
      this.lastProgressAt = performance.now();
      this.note.hidden = true;
    }
    this.track.classList.toggle('is-indeterminate', f === null);
    this.fill.style.transform = `scaleX(${f ?? 0})`;
    const label = percentLabel(fraction);
    this.percent.textContent = label ?? '';
    this.track.setAttribute('aria-valuetext', label ?? message ?? this.message.textContent ?? '');
    if (f === null) this.track.removeAttribute('aria-valuenow');
    else this.track.setAttribute('aria-valuenow', String(Math.round(f * 100)));
  }

  /** Something essential failed: say so plainly and offer Try again / Reload. */
  fail(failure: LoadingFailure): void {
    if (!this.root || this.hiding) this.mount(document.body);
    this.retryHandler = failure.retry;
    this.errorCode.textContent = `code ${failure.code}`;
    this.root!.classList.add('is-failed');
    this.errorBox.hidden = false;
    this.note.hidden = true;
    this.errorBox.querySelector<HTMLButtonElement>('.pk-load__retry')?.focus({ preventScroll: true });
  }

  /** Fades out, then leaves the page. Resolves once it's gone. */
  hide(): Promise<void> {
    if (!this.root) return Promise.resolve();
    if (this.hiding) return this.hiding;
    const root = this.root;
    root.classList.add('is-leaving');
    window.clearInterval(this.stallTimer);
    this.hiding = new Promise((resolve) =>
      window.setTimeout(() => {
        root.remove();
        if (this.root === root) this.root = null;
        this.hiding = null;
        resolve();
      }, FADE_MS),
    );
    return this.hiding;
  }

  private clearError(): void {
    if (!this.root) return;
    this.root.classList.remove('is-failed');
    this.errorBox.hidden = true;
    this.retryHandler = null;
  }

  private mount(host: HTMLElement): void {
    this.root?.remove();
    this.hiding = null;
    const root = el('div', 'pk-load');
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    if (calmLoading(readSettings(), window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)) root.classList.add('is-calm');

    const sky = el('div', 'pk-load__sky');
    sky.setAttribute('aria-hidden', 'true');
    SKY_GLYPHS.forEach((glyph, i) => {
      const s = el('span', `pk-load__star is-hue-${i % 3}`, glyph);
      s.style.left = `${(i * 37 + 9) % 94}%`;
      s.style.top = `${(i * 53 + 13) % 86}%`;
      s.style.animationDelay = `${-(i * 0.9).toFixed(1)}s`;
      sky.appendChild(s);
    });

    const center = el('div', 'pk-load__center');
    const art = el('div', 'pk-load__art');
    art.append(pancakeSvg(), el('span', 'pk-load__heart', '♥'));
    const title = el('p', 'pk-load__title', TEXT.title);
    this.message = el('p', 'pk-load__message', TEXT.preparing);
    this.track = el('div', 'pk-load__track is-indeterminate');
    this.track.setAttribute('role', 'progressbar');
    this.track.setAttribute('aria-valuemin', '0');
    this.track.setAttribute('aria-valuemax', '100');
    this.fill = el('div', 'pk-load__fill');
    this.track.append(this.fill, el('div', 'pk-load__slider'));
    this.percent = el('span', 'pk-load__percent');
    this.note = el('p', 'pk-load__note', TEXT.slow);
    this.note.hidden = true;

    this.errorBox = el('div', 'pk-load__error');
    this.errorBox.hidden = true;
    this.errorCode = el('span', 'pk-load__code');
    const retry = el('button', 'pk-load__button pk-load__retry', TEXT.retry);
    retry.type = 'button';
    retry.addEventListener('click', () => {
      const again = this.retryHandler;
      this.show(this.message.textContent ?? TEXT.preparing);
      again?.();
    });
    const reload = el('button', 'pk-load__button pk-load__reload', TEXT.reload);
    reload.type = 'button';
    reload.addEventListener('click', () => window.location.reload());
    const buttons = el('div', 'pk-load__buttons');
    buttons.append(retry, reload);
    this.errorBox.append(el('p', 'pk-load__error-title', TEXT.failed), el('p', 'pk-load__error-hint', TEXT.failedHint), buttons, this.errorCode);

    center.append(art, title, this.message, this.track, this.percent, this.note, this.errorBox);
    root.append(sky, center);
    host.appendChild(root);
    this.root = root;
    this.lastFraction = null;
    this.lastProgressAt = performance.now();
    window.clearInterval(this.stallTimer);
    this.stallTimer = window.setInterval(() => {
      if (!this.root || this.root.classList.contains('is-failed')) return;
      if (isStalled(this.lastProgressAt, performance.now())) this.note.hidden = false;
    }, STALL_CHECK_MS);
  }
}

/** The one loading screen: boot puts it up, the title takes it down, Continue may raise it again. */
export const loadingScreen = new LoadingScreen();
